import React from "react";
import { FiUpload } from "react-icons/fi";
import { BsFileEarmarkSpreadsheet, BsFileEarmarkText } from "react-icons/bs";
import { FaBriefcase, FaBuilding, FaUserTie } from "react-icons/fa";
import { useBulkUpload } from "../hooks/useBulkUpload";
import DataPreview from "./DataPreview";
import ProgressTracker from "./ProgressTracker";
import ResultsSummary from "./ResultsSummary";
import "../BulkUpload.css";

const EmployeeBulkUpload = ({ corpId, corpName, loadingGif }) => {
  const {
    fileInputRef,
    uploadState,
    file,
    parsedData,
    validationResults,
    uploadProgress,
    uploadResults,
    dragActive,
    isLoading,
    handleDrag,
    handleDrop,
    handleFileInput,
    handleBulkUpload,
    resetUpload,
    downloadTemplate
  } = useBulkUpload("Employee", corpId, corpName);

  return (
    <div className="bulk-upload-container employee-bulk-upload">
      {isLoading && (
        <div className="loading-overlay">
          <img src={loadingGif} alt="Loading..." className="loading-gif" />
        </div>
      )}

      <div className="bulk-upload-header employee-header">
        <div className="header-icon">
          <FaBriefcase size={48} color="#28a745" />
        </div>
        <h2>
          <FaUserTie className="inline-icon" />
          Employee Bulk Registration
        </h2>
        <p className="header-description">
          Upload CSV or Excel files to register multiple employees at once. 
          Each employee record should include department and contact information.
        </p>
        
        <div className="registration-info">
          <div className="info-card">
            <FaBuilding className="info-icon" />
            <div className="info-content">
              <h4>Corporate Organization</h4>
              <p>{corpName}</p>
            </div>
          </div>
        </div>

        <div className="template-download-section">
          <h3>📥 Download Employee Template</h3>
          <p>Use our employee template with department and contact fields:</p>
          <div className="template-buttons">
            <button
              className="template-download-btn employee-btn"
              onClick={() => downloadTemplate("Employee")}
            >
              📄 Download Employee Template (CSV)
            </button>
            <a
              href="/templates/README.md"
              target="_blank"
              rel="noopener noreferrer"
              className="template-help-link"
            >
              📖 View Template Guide
            </a>
          </div>
          <div className="template-info">
            <h4>Employee Template Includes:</h4>
            <ul>
              <li>Employee personal details (Name, Department)</li>
              <li>Direct contact information (Mobile Number)</li>
              <li>Address and location details</li>
              <li>Pickup and drop coordinates</li>
            </ul>
          </div>
        </div>
      </div>

      {uploadState === "idle" && (
        <div className="upload-section employee-upload">
          <div
            className={`file-drop-zone employee-drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              <div className="file-icons">
                <BsFileEarmarkSpreadsheet size={48} />
                <BsFileEarmarkText size={48} />
              </div>
              <h3>Drop your employee data file here or click to browse</h3>
              <p>Supports CSV, Excel (.xlsx, .xls) files with employee and department information</p>
              <div className="upload-requirements">
                <h4>File Requirements:</h4>
                <ul>
                  <li>Include employee name and department information</li>
                  <li>Employee mobile number is required</li>
                  <li>Department names should be clear and consistent</li>
                  <li>Mobile numbers should be 9 digits (without country code)</li>
                </ul>
              </div>
              <FiUpload size={24} />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {uploadState === "parsing" && (
        <div className="parsing-section employee-parsing">
          <div className="parsing-indicator">
            <div className="spinner employee-spinner"></div>
            <h3>Processing employee data...</h3>
            <p>Validating employee and department information from {file?.name}</p>
            <div className="parsing-steps">
              <div className="step">✓ Reading file format</div>
              <div className="step">⏳ Validating employee records</div>
              <div className="step">⏳ Checking department information</div>
              <div className="step">⏳ Preparing preview</div>
            </div>
          </div>
        </div>
      )}

      {uploadState === "preview" && (
        <DataPreview
          data={parsedData}
          validationResults={validationResults}
          type="Employee"
          onUpload={handleBulkUpload}
          onCancel={resetUpload}
          fileName={file?.name}
        />
      )}

      {uploadState === "uploading" && (
        <ProgressTracker
          progress={uploadProgress}
          type="Employee"
        />
      )}

      {uploadState === "complete" && (
        <ResultsSummary
          results={uploadResults}
          type="Employee"
          onReset={resetUpload}
        />
      )}
    </div>
  );
};

export default EmployeeBulkUpload;
